import { MicrophonePermissionState } from './useMicrophonePermission';

/**
 * Analytics for users silently blocked from starting/resuming a recording.
 * One event, `reason` discriminates the cause. Fired with
 * `trigger: 'state_entry'` (prevalence) or `trigger: 'click'` (intent —
 * pointer-down on a disabled start button).
 */
export const RECORDING_START_BLOCKED_EVENT = 'recording_start_blocked';

export type RecordingStartBlockedReason =
  | 'insufficient_storage'
  | 'mic_permission_denied'
  | 'mic_permission_unanswered'
  | 'no_mic_detected'
  | 'no_mic_selected'
  | 'mic_disconnected'
  | 'no_audio_detected'
  | 'no_audio_stream'
  | 'another_session_active'
  | 'no_template_selected'
  | 'not_appointment_owner'
  | 'unsupported_browser';

export type RecordingStartBlockedAction = 'start' | 'resume';

export type RecordingStartBlocked = {
  action: RecordingStartBlockedAction;
  reason: RecordingStartBlockedReason;
};

export type EmbeddedStartBlockedInputs = {
  /** `storageDecision === 'block'` from the recording state */
  storageBlocked: boolean;
  /** devices request failed with MIC_PERMISSIONS_DENIED */
  missingPermissions: boolean;
  isLoadingDevices: boolean;
  deviceCount: number;
  selectedMicId: string | null | undefined;
  /** selected mic is present in the current device list (stale after unplug) */
  isSelectedMicAvailable: boolean;
  hasValidAudioSource: boolean;
  microphoneStatus: 'connected' | 'disconnected' | 'muted' | undefined;
  recordingPhase: 'recording' | 'paused' | 'idle' | undefined;
};

/**
 * Blocked-start reason for the extension widget (thumb + floating window).
 * Dispatches to a per-action deriver; each mirrors its disable chain in
 * useRecordingSessionActions. Deliberately no gate on the machine's
 * validating state — failed/muted mics can park validation forever and must
 * still report; the caller's persistence debounce absorbs startup flicker.
 */
export const deriveEmbeddedStartBlockedReason = (
  inputs: EmbeddedStartBlockedInputs,
): RecordingStartBlocked | undefined => {
  if (inputs.recordingPhase === 'recording') {
    return undefined;
  }
  return inputs.recordingPhase === 'paused'
    ? deriveResumeBlocked(inputs)
    : deriveStartBlocked(inputs);
};

// Plain resume mirrors isResumeDisabled: storage/permissions only — it
// reuses the original stream, so device selection and audio signal never
// gate it. The disconnect surface swaps in resumeWithMic, which mirrors
// isResumeWithMicDisabled instead.
const deriveResumeBlocked = ({
  storageBlocked,
  missingPermissions,
  microphoneStatus,
  selectedMicId,
  isSelectedMicAvailable,
  hasValidAudioSource,
}: EmbeddedStartBlockedInputs): RecordingStartBlocked | undefined => {
  const action = 'resume' as const;
  if (storageBlocked) {
    return { action, reason: 'insufficient_storage' };
  }
  if (missingPermissions) {
    return { action, reason: 'mic_permission_denied' };
  }
  if (microphoneStatus !== 'disconnected') {
    return undefined;
  }
  // Disconnect surface: blocked until an available replacement mic is
  // selected and produces audio. A stale selection (device unplugged) is
  // still the disconnect story; a present-but-silent replacement matches
  // the surface's "no audio" tooltip.
  if (!selectedMicId || !isSelectedMicAvailable) {
    return { action, reason: 'mic_disconnected' };
  }
  if (!hasValidAudioSource) {
    return { action, reason: 'no_audio_detected' };
  }
  return undefined;
};

// Mirrors isStartDisabled: storage → permissions → device presence/selection
// → audio signal. Storage/permission blocks don't depend on device
// enumeration or audio validation, so transient states must not suppress
// them (a pending permission dialog parks validation indefinitely).
const deriveStartBlocked = ({
  storageBlocked,
  missingPermissions,
  isLoadingDevices,
  deviceCount,
  selectedMicId,
  hasValidAudioSource,
}: EmbeddedStartBlockedInputs): RecordingStartBlocked | undefined => {
  const action = 'start' as const;
  if (storageBlocked) {
    return { action, reason: 'insufficient_storage' };
  }
  if (missingPermissions) {
    return { action, reason: 'mic_permission_denied' };
  }
  // Device-presence guards below are meaningless mid-enumeration
  if (isLoadingDevices) {
    return undefined;
  }
  if (deviceCount === 0) {
    return { action, reason: 'no_mic_detected' };
  }
  if (!selectedMicId) {
    return { action, reason: 'no_mic_selected' };
  }
  if (!hasValidAudioSource) {
    return { action, reason: 'no_audio_detected' };
  }
  return undefined;
};

export type WebStartBlockedInputs = {
  permissionState: MicrophonePermissionState;
  /** machine has a supported audio format for this browser */
  isSupportedBrowser: boolean;
  selectedMicId: string | null | undefined;
  hasAudioStream: boolean;
  /** machine context; undefined until validation ran — only false blocks */
  hasValidAudioSource: boolean | undefined;
  isTemplateSelected: boolean;
  isUserApptOwner: boolean;
  isAnyAppointmentInSession: boolean;
};

/**
 * Blocked-start reason for the web new-appointment form. Mirrors the
 * canStartSession guard terms plus the permission states that replace the
 * mic settings UI entirely. 'undetermined' permission is the initial
 * pre-query value → transient, no reason. Deliberately no gate on the
 * machine's validating state: failed acquisition (busy mic) parks validation
 * forever with the error swallowed, and a muted mic never produces sound to
 * exit it — both must still report. Startup flicker is handled by
 * hasValidAudioSource staying undefined until validation observes silence,
 * plus the caller's persistence debounce.
 */
export const deriveWebStartBlockedReason = (
  inputs: WebStartBlockedInputs,
): RecordingStartBlockedReason | undefined => {
  const {
    permissionState,
    isSupportedBrowser,
    selectedMicId,
    hasAudioStream,
    hasValidAudioSource,
    isTemplateSelected,
    isUserApptOwner,
    isAnyAppointmentInSession,
  } = inputs;

  if (!isSupportedBrowser) {
    return 'unsupported_browser';
  }
  if (permissionState === 'undetermined') {
    return undefined;
  }
  if (permissionState === 'denied') {
    return 'mic_permission_denied';
  }
  if (permissionState === 'prompt') {
    return 'mic_permission_unanswered';
  }
  if (permissionState === 'no-microphone-devices') {
    return 'no_mic_detected';
  }
  if (isAnyAppointmentInSession) {
    return 'another_session_active';
  }
  if (!selectedMicId) {
    return 'no_mic_selected';
  }
  // Mic-settings acquisition failed — permission is fine but the device is
  // unusable (busy/exclusive-mode mic, driver failure)
  if (!hasAudioStream) {
    return 'no_audio_stream';
  }
  if (hasValidAudioSource === false) {
    return 'no_audio_detected';
  }
  if (!isTemplateSelected) {
    return 'no_template_selected';
  }
  if (!isUserApptOwner) {
    return 'not_appointment_owner';
  }
  return undefined;
};
